import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { submitTelegramEmail } from '@/shared/api/channels/telegramPersonal';
import { Form, FormGenerator } from '@/shared/ui';
import { FormFieldValues } from '@/shared/ui/FormGenerator';
import { CustomTypography } from '@/shared/ui/Typography';

import { useTelegramAuthAction } from '../../model/telegramPersonal/useTelegramAuthAction';
import { useTelegramAuth } from './TelegramAuthContext';

export const TELEGRAM_PERSONAL_EMAIL_FORM_ID = 'telegram-personal-email-form';

export const TelegramPersonalEmail = ({ channelId }: { channelId: string }) => {
  const { t } = useTranslation();
  const { applyAuthResult, setIsLoading } = useTelegramAuth();
  const { mutateAsync, isPending } = useTelegramAuthAction(
    (data: { email: string }) => submitTelegramEmail(channelId, data)
  );

  useEffect(() => {
    setIsLoading(isPending);
  }, [isPending, setIsLoading]);

  const handleSubmit = async (data: { email: string }) => {
    applyAuthResult(await mutateAsync(data));
  };

  return (
    <Form
      id={TELEGRAM_PERSONAL_EMAIL_FORM_ID}
      schema={z.object({
        email: z.string().trim().email(t('channels:tgPersonal.emailInvalid')),
      })}
      onSubmit={handleSubmit}
      mode="onChange"
    >
      <CustomTypography variant="body1" sx={{ mb: 2 }}>
        {t('channels:tgPersonal.emailSetupDescription')}
      </CustomTypography>
      <FormGenerator
        formScheme={[
          {
            name: 'email',
            label: t('channels:tgPersonal.emailLabel'),
            type: FormFieldValues.Default,
            inputProps: { type: 'email', autoComplete: 'email' },
          },
        ]}
      />
    </Form>
  );
};
